// @ts-check

import { isCustomEnterprisePlan } from "@functions/workspacePlans";
import {
    getIconForPlan,
    getTitleForPlan,
    getDescriptionForPlan,
    getTaglineForPlan,
    getSupportForPlan
} from "@functions/plans/utilities";
import moment from "moment";
import { getTeamMemberRoles } from "../../react/components/TeamMember/getTeamMemberRoles";
import { workspace as workspaceUtils } from "@rflocal/shared";

export function hasRBACAddon(usage) {
    return (
        usage &&
        usage.plan === "starter" &&
        usage.features.rbac &&
        usage.features.annotationReview &&
        usage.limit.labelers
    );
}

export function getBaseRoleDisplayName(role) {
    if (role === "owner") return "Admin";
    if (role === "labeler") return "Labeler";
    if (role === "reviewer") return "Reviewer";

    return role;
}

export function getRoleDisplayName(role, workspace) {
    if (workspace?.customRolesEnabled && workspace?.customRoles?.[role]?.roleDisplayName) {
        return workspace?.customRoles?.[role]?.roleDisplayName;
    }

    return getBaseRoleDisplayName(role);
}

export function getWorkspacePlan(usage) {
    return usage?.plan;
}

export function isCreditSystemEnabled(usage) {
    return usage?.features?.creditSystem || false;
}

export function canEnableOverages(usage) {
    const plan = getWorkspacePlan(usage);

    return ["public", "sandbox", "research"].includes(plan);
}

export function isEnterprisePlan(usage) {
    const plan = getWorkspacePlan(usage);
    return ["growth", "enterprise"].includes(plan);
}

export function isTrial(usage) {
    return usage?.trial?.isTrial;
}

export function isPaidPlanWithSelfServeSubscription(usage) {
    const plan = getWorkspacePlan(usage);
    return ["starter", "basic", "core", "growth-ubp"].includes(plan);
}

// Includes trial users
export function isPaidPlan(usage) {
    const plan = getWorkspacePlan(usage);
    return [
        "starter",
        "pro",
        "enterprise",
        "basic",
        "core",
        "growth",
        "growth-ubp",
        "trial"
    ].includes(plan);
}

// Excludes trial users
export function isPaidPlanNotTrial(usage) {
    const plan = getWorkspacePlan(usage);
    return ["starter", "pro", "enterprise", "basic", "core", "growth", "growth-ubp"].includes(plan);
}

export function canUpgradePlan(usage) {
    return [
        "sandbox",
        "public",
        "starter",
        "research",
        "basic",
        "core",
        "growth-ubp",
        "trial"
    ].includes(usage?.plan);
}

export function hasPublicData(usage) {
    return ["public", "sandbox", "research"].includes(usage?.plan);
}

/**
 * @param {Object} usage
 * @param {WorkspaceMembersMap} members - The members of the workspace
 * @returns {number} - The total remaining seats
 */
export function getTotalRemainingSeats(usage, members) {
    // e.g. 3 labelers, 2 owners, 1 reviewer, 10 roboflow admins (don't count towards limit)
    const membersCountingTowardUsageLimit =
        workspaceUtils.members.getMembersCountingTowardUsageLimitMap(members);

    let totalSeats = usage?.limit?.collaborators ?? 0;

    // Edge case for starter plans with RBAC addon
    if (hasRBACAddon(usage)) {
        totalSeats += usage?.limit?.labelers ?? 0;
        totalSeats += usage?.limit?.reviewers ?? 0;
    }

    // e.g. 3 labelers + 2 owners + 1 reviewer = 6 total used seats
    const totalUsedSeats = Object.keys(membersCountingTowardUsageLimit).length;

    // e.g. 20 collaborators limit - 6 total used seats = 14 remaining seats
    return totalSeats - totalUsedSeats;
}

/**
 * @typedef {"owner" | "labeler" | "reviewer"} WorkspaceRole
 * @typedef {Record<string, WorkspaceRole>} WorkspaceRolesMap
 * @param {WorkspaceRolesMap} roles - The roles of the workspace
 * @returns {WorkspaceRolesMap} - The roles that count towards the usage limit
 */
export function getRolesCountingTowardUsageLimitMap(members, roles) {
    const membersThatCountTowardUsageLimit =
        workspaceUtils.members.getMembersCountingTowardUsageLimitMap(members);
    return Object.entries(roles || {}).reduce((acc, [id, role]) => {
        if (membersThatCountTowardUsageLimit[id]) {
            acc[id] = role;
        }
        return acc;
    }, {});
}

/**
 * @typedef {import('@functions/state/types').Workspace} Workspace
 * @param {Workspace} workspace - The workspace
 * @returns {string} - The id of the user who created the workspace
 */
export function findWorkspaceCreatorId(workspace) {
    if (!workspace?.id || !workspace?.roles || !Object.keys(workspace.roles).length) return;
    if (workspace.created_by) {
        return workspace.created_by;
    }
    const personalWorkspaceOwner = Object.keys(workspace.roles).find(
        (userId) => userId === workspace.id
    );
    if (personalWorkspaceOwner) return personalWorkspaceOwner;
    const otherWorkspaceOwner = Object.keys(workspace.roles).find(
        (userId) => getTeamMemberRoles(workspace, userId).primaryRole === "owner"
    );
    return otherWorkspaceOwner;
}

export function canCreateProjectFolders(usage) {
    return true;
}

export function firstFailedInvoicePaymentDate(usage) {
    // Ignore for custom enterprise plans
    if (isCustomEnterprisePlan(usage?.plan)) return;
    if (usage?.features?.ignoreFirstFailedInvoicePaymentDate) return;
    return usage?.firstFailedInvoicePaymentDate;
}

export function restrictedDueToFirstFailedInvoicePaymentDate(usage) {
    const firstFailedInvoice = firstFailedInvoicePaymentDate(usage);
    if (firstFailedInvoice) {
        const sevenDaysFromDate = moment(firstFailedInvoice.toDate()).add(7, "days");
        return moment().utc().isAfter(sevenDaysFromDate);
    }
    return false;
}

/**
 * @param {Object} usage
 * @returns {boolean} - Whether the workspace is restricted
 */
export function getWorkspaceRestricted(usage) {
    if (!usage) return false;

    if (usage.restricted && usage.restricted_reason) {
        return true;
    }

    const failedInvoiceRestricted = restrictedDueToFirstFailedInvoicePaymentDate(usage);
    if (failedInvoiceRestricted) {
        return true;
    }

    return false;
}

/**
 * Returns the email of the workspace creator.
 *
 * @param {Workspace | undefined} workspace
 * @returns {string | undefined}
 */
function workspaceCreatorEmail(workspace) {
    if (!workspace) return undefined;
    return workspace.members[findWorkspaceCreatorId(workspace)].email;
}

/**
 * Returns the emails of the other members in the workspace that
 * are not the workspace creator or the support user.
 *
 * @param {Workspace | undefined} workspace
 * @param {WorkspaceRole | null} role - The role of the members to return
 * @returns {string[]}
 */
function nonCreatorEmails(workspace, role = null) {
    if (!workspace) return [];
    return Object.keys(workspace.roles)
        .filter(
            (userId) =>
                (role ? getTeamMemberRoles(workspace, userId).primaryRole === role : true) &&
                workspace.members[userId] &&
                userId !== findWorkspaceCreatorId(workspace) &&
                workspace.members[userId].email !== "help@roboflow.com"
        )
        .map((userId) => workspace.members[userId].email);
}

/**
 * Returns the primary and secondary emails of the workspace members.
 *
 * @param {Workspace | undefined} workspace
 * @param {WorkspaceRole | null} role - The role of the members to return
 * @returns {Object} - The primary and secondary emails
 */
export function primaryAndSecondaryEmailsForEventTracking(workspace, role = null) {
    const primaryEmail = workspaceCreatorEmail(workspace);
    const secondaryEmails = nonCreatorEmails(workspace, role);
    return { primaryEmail, secondaryEmails: secondaryEmails.sort().join(",") };
}

export const getIconForWorkspacePlan = getIconForPlan;
export const getTitleForWorkspacePlan = getTitleForPlan;
export const getDescriptionForWorkspacePlan = getDescriptionForPlan;
export const getTaglineForWorkspacePlan = getTaglineForPlan;
export const getSupportForWorkspacePlan = getSupportForPlan;
export const memberCountsTowardUsageLimit = workspaceUtils.members.memberCountsTowardUsageLimit;
export const getMembersCountingTowardUsageLimitMap =
    workspaceUtils.members.getMembersCountingTowardUsageLimitMap;
export const overagesAutoEnabled = function (usage) {
    return !canEnableOverages(usage);
};

export default {
    hasRBACAddon,
    getIconForWorkspacePlan,
    getTitleForWorkspacePlan,
    getDescriptionForWorkspacePlan,
    getTaglineForWorkspacePlan,
    getWorkspacePlan,
    getRoleDisplayName,
    canEnableOverages,
    overagesAutoEnabled,
    isEnterprisePlan,
    isCreditSystemEnabled,
    isTrial,
    isPaidPlanWithSelfServeSubscription,
    isPaidPlan,
    isPaidPlanNotTrial,
    getSupportForWorkspacePlan,
    canUpgradePlan,
    hasPublicData,
    memberCountsTowardUsageLimit: workspaceUtils.members.memberCountsTowardUsageLimit,
    getMembersCountingTowardUsageLimitMap:
        workspaceUtils.members.getMembersCountingTowardUsageLimitMap,
    getTotalRemainingSeats,
    getRolesCountingTowardUsageLimitMap,
    findWorkspaceCreatorId,
    firstFailedInvoicePaymentDate,
    restrictedDueToFirstFailedInvoicePaymentDate,
    getWorkspaceRestricted,
    primaryAndSecondaryEmailsForEventTracking,
    canCreateProjectFolders,
    getBaseRoleDisplayName
};
